import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { HomePageTabId } from '../constants/productCategories';

export type SearchCategory = HomePageTabId | 'all';

interface SearchContextValue {
  draftQuery: string;
  draftCategory: SearchCategory;
  appliedQuery: string;
  setDraftQuery: (query: string) => void;
  setDraftCategory: (category: SearchCategory) => void;
  submitSearch: () => SearchCategory;
  clearAppliedSearch: () => void;
  applyCategory: (category: SearchCategory) => void;
  restoreSearch: (query: string, category: SearchCategory) => void;
}

const SearchContext = createContext<SearchContextValue | undefined>(undefined);

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [draftQuery, setDraftQuery] = useState('');
  const [draftCategory, setDraftCategory] = useState<SearchCategory>('all');
  const [appliedQuery, setAppliedQuery] = useState('');

  const submitSearch = useCallback(() => {
    const nextQuery = draftQuery.trim();
    setAppliedQuery(nextQuery);
    return draftCategory;
  }, [draftCategory, draftQuery]);

  const clearAppliedSearch = useCallback(() => {
    setAppliedQuery('');
    setDraftQuery('');
  }, []);

  const applyCategory = useCallback((category: SearchCategory) => {
    setDraftCategory(category);
    setAppliedQuery('');
    setDraftQuery('');
  }, []);

  const restoreSearch = useCallback((query: string, category: SearchCategory) => {
    setDraftQuery(query);
    setDraftCategory(category);
    setAppliedQuery(query.trim());
  }, []);

  const value = useMemo(
    () => ({
      draftQuery,
      draftCategory,
      appliedQuery,
      setDraftQuery,
      setDraftCategory,
      submitSearch,
      clearAppliedSearch,
      applyCategory,
      restoreSearch,
    }),
    [
      appliedQuery,
      applyCategory,
      clearAppliedSearch,
      draftCategory,
      draftQuery,
      restoreSearch,
      submitSearch,
    ]
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

export function useSearch() {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}
